/**
 * Handing the link over.
 *
 * Three ways, in order of how well they work in the moment: the native share sheet (which puts
 * the link straight into WhatsApp or Messages), a copy button, and the QR code on screen.
 */

export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed';

export const canNativeShare = (): boolean => typeof navigator.share === 'function';

/** Copy to the clipboard, with a fallback for browsers that gate the async API. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Permission denied or an insecure context — fall through to the old way.
  }

  try {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(field);
    return copied;
  } catch {
    return false;
  }
}

/**
 * Offer the link. Falls back to copying when there is no share sheet, so the button always
 * does something useful rather than nothing.
 */
export async function shareLink(url: string, text: string): Promise<ShareOutcome> {
  if (canNativeShare()) {
    try {
      await navigator.share({ title: 'Beeline', text, url });
      return 'shared';
    } catch (error) {
      // Dismissing the sheet rejects with AbortError; that is not a failure.
      if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
    }
  }

  return (await copyText(url)) ? 'copied' : 'failed';
}
